import { useDebouncedValue, useInputState } from '@mantine/hooks'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

import type {
  CoinItemInGetList,
  CoinItemInPostList,
  FetchCoinsResponse,
} from '@/services/coin/coins'
import {
  coinsByIdsQueryOptions,
  coinsQueryOptions,
  popularCoinsQueryOptions,
  TopMoversDirection,
  topMoversQueryOptions,
  topWatchListQueryOptions,
  TradablePlatform,
  trendingCoinQueryOptions,
} from '@/services/coin/coins'
import useGlobalStore from '@/stores/global'
import type { CoinId, CoinOverall, PopularCoin, TopMoverItem, TrendingCoin } from '@/types/coin'
import { VisitType } from '@/types/coin'

export enum CoinListType {
  TopGainers,
  TopLosers,
  RecentlyAdded,
  Trending,
  TokensToWatch,
  MostPopular,
}

export interface CoinItem {
  id: CoinId
  name: string
  symbol: string
  usdPrice: number
  slug: string
  percentChange: number | null
  prices?: number[]
}
const TOP_DISPLAY_COUNT = 20

function useLazyLoadCoins<T>({
  queryOptions,
  type,
  activeCoinListType,
}: {
  type: CoinListType
  queryOptions: UseQueryOptions<any, any, any, any>
  activeCoinListType: CoinListType
}): {
  isFetching: boolean
  data: T | undefined
} {
  const { data, isFetching, refetch } = useQuery<T>({
    ...queryOptions,
    enabled: false,
  })
  useEffect(() => {
    if (activeCoinListType === type && !data) {
      refetch()
    }
  }, [activeCoinListType, data, refetch])
  return {
    data,
    isFetching,
  }
}

/**
 * only when activeCoinListType is current type would try to request coins
 */
export function useLazyLoadCoinsWithPrices<
  Data extends
    | FetchCoinsResponse
    | TopMoverItem[]
    | PopularCoin[]
    | CoinItemInPostList[]
    | TrendingCoin[]
    | CoinId[],
>({
  queryOptions,
  type,
  activeCoinListType,
  key,
}: {
  type: CoinListType
  queryOptions: UseQueryOptions<any, any, any, any>
  activeCoinListType: CoinListType
  key?: string
}) {
  // get ids first
  const { data: idSources, isFetching: isIdSourcesFetching } = useLazyLoadCoins<Data>({
    queryOptions,
    type,
    activeCoinListType,
  })
  const ids = useMemo<CoinId[]>(() => {
    if (!idSources) return []
    let coins: Data | CoinItemInGetList[] = idSources
    if (typeof idSources === 'object' && idSources !== null && 'data' in idSources) {
      coins = (idSources as FetchCoinsResponse).data
    }
    if (!Array.isArray(coins)) return []
    return coins.map(item => {
      if (key) {
        return (item as Record<string, unknown>)[key] as CoinId
      }
      return item as CoinId
    })
  }, [idSources, key])
  // use id to get token info
  const { data, isFetching } = useQuery(coinsByIdsQueryOptions(ids))
  return {
    data,
    isFetching: isFetching || isIdSourcesFetching,
  }
}

type CoinListUsedKey =
  | 'name'
  | 'symbol'
  | 'prices'
  | 'slug'
  | 'usd_price'
  | 'usd_price_change_24h'
  | 'token_id'

export function useDifferenceTypeCoinList(activeCoinListType: CoinListType) {
  const { data: topGainers, isFetching: isTopGainersFetching } = useLazyLoadCoinsWithPrices<
    TopMoverItem[]
  >({
    type: CoinListType.TopGainers,
    activeCoinListType,
    queryOptions: topMoversQueryOptions({
      depth: TOP_DISPLAY_COUNT,
      direction: TopMoversDirection.DESC,
      tradable_on: TradablePlatform.APP_OR_EXCHANGE,
    }),
    key: 'token_id',
  })
  const { data: topLosers, isFetching: isTopLosesFetching } = useLazyLoadCoinsWithPrices<
    TopMoverItem[]
  >({
    type: CoinListType.TopLosers,
    activeCoinListType,
    queryOptions: topMoversQueryOptions({
      depth: TOP_DISPLAY_COUNT,
      direction: TopMoversDirection.ASC,
      tradable_on: TradablePlatform.APP_OR_EXCHANGE,
    }),
    key: 'token_id',
  })

  const { data: recentlyAdded, isFetching: isRecentlyAddedFetching } =
    useLazyLoadCoinsWithPrices<FetchCoinsResponse>({
      type: CoinListType.RecentlyAdded,
      activeCoinListType,
      key: 'token_id',
      queryOptions: coinsQueryOptions({
        page: 0,
        limit: TOP_DISPLAY_COUNT,
        sort: 'createTime',
        direction: 'DESC',
      }),
    })
  const { data: trendingTokens, isFetching: isTrendingTokensFetching } = useLazyLoadCoinsWithPrices<
    TrendingCoin[]
  >({
    type: CoinListType.Trending,
    activeCoinListType,
    key: 'token_id',
    queryOptions: trendingCoinQueryOptions(TOP_DISPLAY_COUNT),
  })
  const { data: topWatchList, isFetching: isTopWatchListFetching } = useLazyLoadCoinsWithPrices<
    CoinId[]
  >({
    type: CoinListType.TokensToWatch,
    activeCoinListType,
    queryOptions: topWatchListQueryOptions,
  })
  const { data: popularTokens, isFetching: isPopularTokensFetching } = useLazyLoadCoinsWithPrices<
    PopularCoin[]
  >({
    type: CoinListType.MostPopular,
    activeCoinListType,
    queryOptions: popularCoinsQueryOptions(VisitType.Token),
    key: 'key',
  })

  const displayCoinRes = useMemo<{
    isLoading: boolean
    coins: CoinItem[]
  }>(() => {
    const { isLoading, coins } = (() => {
      let isLoading = false
      let coins: Pick<CoinOverall, CoinListUsedKey>[] | undefined = void 0
      if (activeCoinListType === CoinListType.TopGainers) {
        isLoading = isTopGainersFetching
        coins = topGainers
      } else if (activeCoinListType === CoinListType.TopLosers) {
        isLoading = isTopLosesFetching
        coins = topLosers
      } else if (activeCoinListType === CoinListType.RecentlyAdded) {
        isLoading = isRecentlyAddedFetching
        coins = recentlyAdded
      } else if (activeCoinListType === CoinListType.Trending) {
        isLoading = isTrendingTokensFetching
        coins = trendingTokens
      } else if (activeCoinListType === CoinListType.TokensToWatch) {
        isLoading = isTopWatchListFetching
        coins = topWatchList
      } else if (activeCoinListType === CoinListType.MostPopular) {
        isLoading = isPopularTokensFetching
        coins = popularTokens
      }

      return { isLoading, coins: coins || [] }
    })()
    return {
      isLoading,
      coins: coins.map(item => ({
        name: item.name,
        symbol: item.symbol,
        usdPrice: item.usd_price,
        percentChange: item.usd_price_change_24h,
        prices: item.prices,
        slug: item.slug,
        id: item.token_id,
      })),
    }
  }, [
    activeCoinListType,
    topGainers,
    isTopGainersFetching,
    topLosers,
    isTopLosesFetching,
    recentlyAdded,
    isRecentlyAddedFetching,
    trendingTokens,
    isTrendingTokensFetching,
    topWatchList,
    isTopWatchListFetching,
    popularTokens,
    isPopularTokensFetching,
  ])

  return {
    displayCoinRes,
  }
}

const MAX_RESULT_LIMIT = 20

export function useSearchCoins() {
  const [keyword, setKeyword] = useInputState<string>('')

  const allCoins = useGlobalStore(state => state.allCoins)
  const allCoinsStatus = useGlobalStore(state => state.allCoinsStatus)
  const isFetchingAllToken = allCoinsStatus === 'pending'

  // TODO: Implement sorting logic on allCoins
  const sortedTokens = allCoins

  const matchedKeywordTokenIds = useMemo(() => {
    if (!keyword) return []
    return sortedTokens
      .filter(
        ({ name, symbol }) =>
          name.toLocaleLowerCase().includes(keyword.toLowerCase()) ||
          symbol.toLowerCase().includes(keyword.toLowerCase()),
      )
      .slice(0, MAX_RESULT_LIMIT)
      .map(({ id }) => id)
  }, [keyword, sortedTokens])

  const [debouncedMatchedKeywordTokenIds] = useDebouncedValue(matchedKeywordTokenIds, 500)

  const { data: searchResult, isFetching: isSearching } = useQuery(
    coinsByIdsQueryOptions(debouncedMatchedKeywordTokenIds),
  )
  const coinList = useMemo(() => {
    if (!searchResult) return []
    return searchResult.map(item => ({
      id: item.token_id,
      name: item.name,
      symbol: item.symbol,
      usdPrice: item.usd_price,
      percentChange: item.usd_price_change_24h,
      prices: item.prices,
      slug: item.slug,
    }))
  }, [searchResult])

  /**
   * when would searching display loading first
   * coinList's isSearching is async
   */
  const isWouldSearching =
    !!keyword &&
    matchedKeywordTokenIds.length > 0 &&
    matchedKeywordTokenIds.join(',') !== coinList.map(coin => coin.id).join(',')

  return {
    keyword,
    setKeyword,
    coinList,
    isSearching: isFetchingAllToken || isWouldSearching || isSearching,
  }
}
